// src/components/common/IncompleteProfileBanner.js
// Banner "soft" que insiste con completar el perfil.
//
// Comportamiento:
//   - Se auto-monta en App.js (nivel global).
//   - Consulta el perfil del usuario logueado al montarse y al cambiar de ruta.
//   - Si el perfil esta incompleto → banner fixed bottom con CTA "Completar mi perfil".
//   - Si el perfil se completa → desaparece con animacion.
//   - NO se puede cerrar (por diseno, es "soft pero insistente").
//   - No se muestra si el usuario esta en /complete-profile (evita banner sobre la propia
//     pagina de completar) ni en /login, /register o rutas admin.
//
// Perfil considerado completo cuando tiene:
//   - Tecnico (user_profiles): first_name, last_name, country, birthdate
//   - Empresa (company_profiles): company_name, country
//
// Estos son los mismos campos que valida LoginPage.js antes de redirigir.
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import supabase from '../../supabase';
import { useAuth } from '../../hooks/useAuth';
import { track } from '../../utils/track';

// Rutas donde NO tiene sentido mostrar el banner.
// El match es por startsWith, así que '/bronze-member/profile' también cubre
// '/bronze-member/profile-settings' y '/bronze-member/profile/business-account'.
const HIDDEN_ROUTES = [
  '/complete-profile',
  '/auth/choose-type',
  '/auth/verify',
  '/auth/callback',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  // Perfil ya abierto — el banner sería redundante (el CTA lleva a la misma pantalla)
  '/bronze-member/profile',
  '/company/profile',
  '/profile',
];

const IncompleteProfileBanner = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [isIncomplete, setIsIncomplete] = useState(false);
  const [profileType, setProfileType] = useState(null); // 'individual' | 'company' | null
  // Ultima ruta desde la que consultamos. Solo re-fetcheamos al salir de rutas
  // donde el usuario pudo modificar el perfil (complete-profile, profile).
  const lastCheckedUserRef = useRef(null);
  const previousPathRef = useRef(location.pathname);
  // Para trackear impressions una vez por sesion+ruta, no en cada re-render
  const impressionTrackedRef = useRef(new Set());

  // Rutas donde el usuario pudo haber completado su perfil → hay que re-checkear al salir
  const REFETCH_TRIGGER_PATHS = ['/complete-profile', '/profile', '/company/profile'];

  useEffect(() => {
    if (loading || !isAuthenticated || !user?.id) {
      setIsIncomplete(false);
      lastCheckedUserRef.current = null;
      return;
    }

    // Skip la query si ya la hicimos para este user y no venimos de una ruta
    // donde se pudieran haber editado los datos.
    const cameFromEditRoute = REFETCH_TRIGGER_PATHS.some(p => previousPathRef.current.startsWith(p));
    const alreadyCheckedThisUser = lastCheckedUserRef.current === user.id;
    previousPathRef.current = location.pathname;

    if (alreadyCheckedThisUser && !cameFromEditRoute) return;

    let cancelled = false;
    (async () => {
      try {
        const [{ data: indiv }, { data: comp }] = await Promise.all([
          supabase.from('user_profiles')
            .select('first_name, last_name, country, birthdate')
            .eq('id', user.id).maybeSingle(),
          supabase.from('company_profiles')
            .select('company_name, country')
            .eq('id', user.id).maybeSingle(),
        ]);
        if (cancelled) return;

        lastCheckedUserRef.current = user.id;

        if (indiv) {
          setProfileType('individual');
          const incomplete = !indiv.first_name || !indiv.last_name || !indiv.country || !indiv.birthdate;
          setIsIncomplete(incomplete);
        } else if (comp) {
          setProfileType('company');
          const incomplete = !comp.company_name || !comp.country;
          setIsIncomplete(incomplete);
        } else {
          // Sin perfil de ningun tipo → no mostramos banner (LoginPage lo redirige a choose-type)
          setIsIncomplete(false);
          setProfileType(null);
        }
      } catch (err) {
        console.warn('[IncompleteProfileBanner] error consultando perfil:', err);
        setIsIncomplete(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, location.pathname, loading]);

  // No mostrar en rutas ocultas
  const inHiddenRoute = HIDDEN_ROUTES.some(r => location.pathname.startsWith(r));

  const shouldShow = isAuthenticated && isIncomplete && !inHiddenRoute && !loading;

  // Trackear impression (una vez por combinacion user + ruta, para no explotar events)
  useEffect(() => {
    if (!shouldShow) return;
    const key = `${user?.id || 'anon'}::${location.pathname}`;
    if (impressionTrackedRef.current.has(key)) return;
    impressionTrackedRef.current.add(key);
    track('incomplete_profile_banner_impression', {
      user_type: profileType,
      path: location.pathname,
    });
  }, [shouldShow, user?.id, location.pathname, profileType]);

  const handleCtaClick = () => {
    track('incomplete_profile_banner_click', {
      user_type: profileType,
      from_path: location.pathname,
    });
    navigate('/complete-profile');
  };

  const label = profileType === 'company'
    ? 'Tu empresa está casi lista. Completá tu perfil para que la comunidad te encuentre.'
    : 'Tu perfil está casi listo. Completalo para aparecer en la comunidad de profesionales.';

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          key="incomplete-profile-banner"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 260 }}
          className="fixed bottom-0 left-0 right-0 z-[60] px-3 pb-3 pointer-events-none"
        >
          <div
            role="alert"
            className="pointer-events-auto max-w-4xl mx-auto bg-amber-500 text-amber-950 shadow-2xl rounded-xl border border-amber-600 flex items-center gap-3 px-4 py-3 sm:py-3.5"
          >
            <FiAlertCircle size={22} className="shrink-0" />
            <p className="flex-1 text-sm sm:text-base font-medium leading-snug">
              {label}
            </p>
            <button
              type="button"
              onClick={handleCtaClick}
              className="shrink-0 inline-flex items-center gap-1.5 bg-amber-950 hover:bg-black text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              Completar ahora
              <FiArrowRight size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IncompleteProfileBanner;
